import { COLOSSUS, INCURSION_TIERS } from '@somnhold/content';
import { cellAtWorld, cellCenter } from '../grid.ts';
import { hexToWorld } from '../hex.ts';
import { cosA, idiv, mulBp, sinA, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, Zone } from '../types.ts';
import { damageHero, ev, markedBlow } from './combat.ts';
import { heroHome, postSpot } from './create.ts';
import { simaOnGlass } from './sima.ts';

/*
 * Obsidiana, incursion level III (docs/NIVELES.md): black glass that cracks under a hero who stands still
 * and gives way a moment later, shards where inked nightmares fall, and the obsidian colossus, which shines
 * and turns blows back on whoever strikes it. In the deep crypt the glass covers its third floor.
 */

const T3 = INCURSION_TIERS[3];

/** Whether the glass is under this hero: everywhere in an Obsidiana incursion, on the third floor of the deep crypt. */
const onGlass = (s: BattleState, h: Hero): boolean => {
  if (s.rules?.tier !== 3) return false;
  const L = s.map.dungeon;
  if (!L) return !s.map.sectors.some((sec) => withinRange(postSpot(s, sec.index).x, postSpot(s, sec.index).y, h.x, h.y, T3.postClear)) && !withinRange(heroHome(s, -1).x, heroHome(s, -1).y, h.x, h.y, T3.postClear);
  // The deep crypt: the third floor's rooms (not its entrance, not the glass bridge, which has its own rule).
  if (s.dungeon?.sima) return simaOnGlass(s, h);
  const floor = s.dungeon?.deep?.floor ?? 0;
  if (floor !== 2) return false;
  return L.rooms.some((r) => r.floor === 2 && r.kind !== 'entry' && !r.puzzle && withinRange(hexToWorld(r.q, r.r).x, hexToWorld(r.q, r.r).y, h.x, h.y, r.radius * 1000 + 300));
};

/** Each tick: glass cracks under heroes standing still, and the colossus shines and slams. */
export const obsidianTick = (s: BattleState): void => {
  if (s.rules?.tier !== 3 || s.result !== 'running') return;
  const fight = s.enemies.some((e) => e.hp > 0);
  for (const h of s.heroes) {
    if (h.downTicks > 0) {
      h.glassT = 0;
      continue;
    }
    const cell = cellAtWorld(s, h.x, h.y);
    if (cell !== h.stillCell) {
      h.stillCell = cell;
      h.glassT = 0;
      continue;
    }
    // Only while there is a fight: walking around an empty field is not a trap.
    if (!fight || !onGlass(s, h)) continue;
    h.glassT = (h.glassT ?? 0) + 1;
    if (h.glassT < T3.glassStill) continue;
    h.glassT = 0;
    if (s.zones.some((z) => z.kind === 'glass' && z.cells[0] === cell)) continue;
    const c = cellCenter(s.map.radius, cell);
    s.zones.push({ id: s.nextId++, kind: 'glass', x: c.x, y: c.y, radius: T3.glassRadius, cells: [cell], ticks: T3.glassWarn + T3.pitTicks, every: 6, damage: T3.pitDamage, slowBp: 0, pid: '', warn: T3.glassWarn });
  }
  for (const e of s.enemies) if (e.kind === 'colossus' && e.hp > 0) colossusTick(s, e);
};

/** The colossus's rhythm: a warning glint, the shine, then the slam that throws a ring of shards. */
const colossusTick = (s: BattleState, e: Enemy): void => {
  const age = s.tick - e.born;
  const k = age % COLOSSUS.every;
  if (age < COLOSSUS.every) return;
  if (k === COLOSSUS.every - COLOSSUS.warnTicks) ev(s, { t: 'skill', hero: e.id, skill: 'colossusGlint', x: e.x, y: e.y });
  if (k === 0) {
    e.shineUntil = s.tick + COLOSSUS.shineTicks;
    ev(s, { t: 'skill', hero: e.id, skill: 'colossusShine', x: e.x, y: e.y });
  }
  if (k === COLOSSUS.shineTicks) {
    ev(s, { t: 'skill', hero: e.id, skill: 'colossusSlam', x: e.x, y: e.y });
    for (const h of s.heroes) {
      if (h.downTicks !== 0 || !withinRange(e.x, e.y, h.x, h.y, COLOSSUS.slamRadius)) continue;
      damageHero(s, h, mulBp(COLOSSUS.slamDamage, s.dungeon?.dmgBp ?? 10000), e);
      markedBlow(s, h, 'glassBlood');
    }
    ev(s, { t: 'boom', x: e.x, y: e.y, r: COLOSSUS.slamRadius, kind: 'colossusSlam' });
    for (let i = 0; i < COLOSSUS.ringShards; i++) {
      const a = idiv(i * 1024, COLOSSUS.ringShards) + (s.tick % 171);
      dropShards(s, e.x + idiv(cosA(a) * COLOSSUS.ringRadius, 10000), e.y + idiv(sinA(a) * COLOSSUS.ringRadius, 10000));
    }
  }
};

/** Whether the colossus is shining now (blows bounce). */
export const shining = (s: BattleState, e: Enemy): boolean => e.kind === 'colossus' && (e.shineUntil ?? 0) > s.tick;

/** A blow on a shining colossus: part of it comes back to the hero who struck. */
export const reflect = (s: BattleState, e: Enemy, dmg: number, pid: string | undefined): void => {
  if (!pid || !shining(s, e)) return;
  const h = s.heroes.find((x) => x.pid === pid);
  if (h) damageHero(s, h, mulBp(dmg, COLOSSUS.reflectBp));
};

export const dropShards = (s: BattleState, x: number, y: number): void => {
  s.zones.push({ id: s.nextId++, kind: 'shards', x, y, radius: T3.shardsRadius, cells: [], ticks: T3.shardsTicks, every: 6, damage: T3.shardsDamage, slowBp: 0, pid: '' });
};

/** An inked nightmare falling on the glass leaves its shards. */
export const obsidianDeath = (s: BattleState, e: Enemy): void => {
  if (s.rules?.tier !== 3 || e.tier < 2 || e.kind === 'boneWall') return;
  // On the deep crypt's third floor, or the Chasm's glass.
  if (s.map.dungeon && !(s.dungeon?.sima ? s.dungeon.sima.stratum === 'glass' : (s.dungeon?.deep?.floor ?? 0) === 2)) return;
  dropShards(s, e.x, e.y);
};

/** Glass and shards (in updateZones). */
export const obsidianZone = (s: BattleState, z: Zone): void => {
  if (z.kind === 'shards') {
    if (z.ticks % z.every !== 0) return;
    for (const h of s.heroes) if (h.downTicks === 0 && withinRange(z.x, z.y, h.x, h.y, z.radius)) damageHero(s, h, z.damage);
    return;
  }
  if (z.warn !== undefined && z.warn > 0) {
    z.warn--;
    if (z.warn > 0) return;
    // It gives way: whoever is still on it is cut, and a pit stays.
    for (const h of s.heroes) if (h.downTicks === 0 && withinRange(z.x, z.y, h.x, h.y, z.radius)) damageHero(s, h, T3.glassDamage);
    ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'glassBreak' });
    z.warn = -1;
    return;
  }
  if (z.ticks % z.every !== 0) return;
  for (const h of s.heroes) if (h.downTicks === 0 && withinRange(z.x, z.y, h.x, h.y, z.radius)) damageHero(s, h, z.damage);
};
